import React, { useEffect, useMemo, useState } from 'react';
import { TextField, Button, MenuItem } from '@mui/material';
import DrawerShell from './DrawerShell';
import { FieldGroup } from '../PanelComerciales.styles';
import { panelTheme } from '../PanelComerciales.theme';
import { Commercial, COMMERCIAL_STATUS_OPTIONS } from '../PanelComerciales.types';
import { isCurrency, isNonEmptyTeamName } from '../utils/validators';

export type CommercialEditMode = 'full' | 'team' | 'goal' | 'absence';

interface CommercialEditDrawerProps {
    open: boolean;
    mode: CommercialEditMode;
    commercial: Commercial | null;
    teamOptions: string[];
    onClose: () => void;
    onSave: (next: Commercial) => void;
}

const TITLES: Record<CommercialEditMode, string> = {
    full: 'Editar comercial',
    team: 'Cambiar equipo',
    goal: 'Ajustar meta Q2',
    absence: 'Registrar ausencia',
};

const DESCRIPTION = 'Completa la información y guarda los cambios.';

const CommercialEditDrawer: React.FC<CommercialEditDrawerProps> = ({
    open,
    mode,
    commercial,
    teamOptions,
    onClose,
    onSave,
}) => {
    const [draft, setDraft] = useState<Commercial | null>(commercial);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setDraft(commercial);
        setError(null);
    }, [commercial, open]);

    const title = useMemo(() => TITLES[mode], [mode]);

    if (!draft) return null;

    const setField = <K extends keyof Commercial>(key: K, value: Commercial[K]) =>
        setDraft((prev) => (prev ? { ...prev, [key]: value } : prev));

    const submit = () => {
        if (!draft) return;
        if (mode === 'goal' || mode === 'full') {
            if (!isCurrency(draft.goal)) return setError('Meta inválida');
        }
        if (mode === 'team' || mode === 'full') {
            if (!isNonEmptyTeamName(draft.team)) return setError('Equipo requerido');
        }
        onSave(draft);
        onClose();
    };

    return (
        <DrawerShell
            open={open}
            title={title}
            description={commercial ? `${commercial.name} · ${commercial.team} · ${DESCRIPTION}` : DESCRIPTION}
            onClose={onClose}
        >
            {(mode === 'full') && (
                <FieldGroup>
                    <label>Nombre</label>
                    <TextField
                        size='small'
                        value={draft.name}
                        onChange={(e) => setField('name', e.target.value)}
                    />
                </FieldGroup>
            )}
            {(mode === 'full' || mode === 'team') && (
                <FieldGroup>
                    <label>Equipo</label>
                    <TextField
                        size='small'
                        select
                        value={draft.team}
                        onChange={(e) => setField('team', e.target.value)}
                    >
                        {teamOptions.map((t) => (
                            <MenuItem key={t} value={t}>
                                {t}
                            </MenuItem>
                        ))}
                    </TextField>
                </FieldGroup>
            )}
            {(mode === 'full' || mode === 'goal') && (
                <FieldGroup>
                    <label>Meta Q2</label>
                    <TextField
                        size='small'
                        value={draft.goal}
                        onChange={(e) => setField('goal', e.target.value)}
                        placeholder='$250,000'
                    />
                </FieldGroup>
            )}
            {(mode === 'full') && (
                <FieldGroup>
                    <label>Estado</label>
                    <TextField
                        size='small'
                        select
                        value={draft.status}
                        onChange={(e) => setField('status', e.target.value as Commercial['status'])}
                    >
                        {COMMERCIAL_STATUS_OPTIONS.map((s) => (
                            <MenuItem key={s} value={s}>
                                {s}
                            </MenuItem>
                        ))}
                    </TextField>
                </FieldGroup>
            )}
            {(mode === 'full' || mode === 'absence') && (
                <FieldGroup>
                    <label>Ausencia</label>
                    <TextField
                        size='small'
                        value={draft.absence}
                        onChange={(e) => setField('absence', e.target.value)}
                        placeholder='Ej: Vacaciones 1-15 may'
                    />
                </FieldGroup>
            )}
            {(mode === 'full') && (
                <FieldGroup>
                    <label>Notas internas</label>
                    <TextField
                        size='small'
                        multiline
                        minRows={2}
                        value={draft.notes ?? ''}
                        onChange={(e) => setField('notes', e.target.value)}
                        placeholder='Visible solo para Gerencia Comercial.'
                    />
                </FieldGroup>
            )}
            {error ? <span style={{ color: panelTheme.colors.danger, fontSize: 12 }}>{error}</span> : null}
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <Button onClick={onClose} sx={{ textTransform: 'none' }}>
                    Cancelar
                </Button>
                <Button
                    onClick={submit}
                    variant='contained'
                    sx={{
                        textTransform: 'none',
                        background: panelTheme.colors.indigo,
                        '&:hover': { background: panelTheme.colors.violet },
                    }}
                >
                    Guardar
                </Button>
            </div>
        </DrawerShell>
    );
};

export default CommercialEditDrawer;
